import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = unauth, obj = authed
    const [error, setError] = useState("");

    const fetchMe = useCallback(async () => {
        const token = localStorage.getItem("tf_token");
        if (!token) {
            setUser(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            localStorage.removeItem("tf_token");
            setUser(false);
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            // MFA path — caller must complete /auth/verify-mfa before
            // a session cookie / JWT is issued.
            if (data?.mfa_required) {
                return {
                    ok: true,
                    mfa: {
                        challenge_id: data.challenge_id,
                        delivery_email_masked: data.delivery_email_masked,
                        expires_at: data.expires_at,
                        ttl_seconds: data.ttl_seconds,
                    },
                };
            }
            localStorage.setItem("tf_token", data.token);
            setUser(data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
            setError(msg);
            return { ok: false, error: msg };
        }
    };

    const verifyMfa = async (challengeId, code) => {
        setError("");
        try {
            const { data } = await api.post("/auth/verify-mfa", {
                challenge_id: challengeId,
                code,
            });
            localStorage.setItem("tf_token", data.token);
            setUser(data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            const status = e.response?.status;
            const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
            // 410 = challenge invalid / expired / used → caller should
            // restart the login flow; 401 = wrong code → stay on OTP step.
            return { ok: false, error: msg, expired: status === 410 };
        }
    };

    const resendMfa = async (challengeId) => {
        setError("");
        try {
            const { data } = await api.post("/auth/resend-mfa", {
                challenge_id: challengeId,
            });
            return {
                ok: true,
                mfa: {
                    challenge_id: data.challenge_id,
                    delivery_email_masked: data.delivery_email_masked,
                    expires_at: data.expires_at,
                    ttl_seconds: data.ttl_seconds,
                },
            };
        } catch (e) {
            const status = e.response?.status;
            const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
            return { ok: false, error: msg, expired: status === 410 };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch { /* ignore */ }
        localStorage.removeItem("tf_token");
        setUser(false);
    };

    const permSet = useMemo(() => {
        if (!user || typeof user !== "object") return new Set();
        return new Set(user.permissions || []);
    }, [user]);

    const hasPermission = useCallback(
        (...perms) => {
            // super_admin is always allowed (defensive — backend already grants all)
            if (user?.role === "super_admin") return true;
            return perms.every((p) => permSet.has(p));
        },
        [permSet, user],
    );

    const hasAnyPermission = useCallback(
        (...perms) => {
            if (user?.role === "super_admin") return true;
            return perms.some((p) => permSet.has(p));
        },
        [permSet, user],
    );

    return (
        <AuthContext.Provider
            value={{ user, login, verifyMfa, resendMfa, logout, error, refresh: fetchMe, hasPermission, hasAnyPermission }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
